import { useSyncExternalStore } from "react";
import { api, coverUrl } from "@/lib/api";
import { dominantColor, palette, rgb } from "@/lib/artColor";
import { onPrefs, pushPref } from "@/lib/prefsSync";

export const HOLIDAYS = [
  { id: "newyear", label: "New Year's", colors: ["#0b1a3a", "#c9a227", "#f2e6b1", "#1f3a73"], from: [12, 26], to: [1, 7] },
  { id: "valentine", label: "Valentine's Day", colors: ["#f6c1cc", "#d9365e", "#ffe4ea", "#a8213f"], from: [2, 7], to: [2, 15] },
  { id: "stpatrick", label: "St. Patrick's Day", colors: ["#1b7f3b", "#6fcf7a", "#e7f6e3", "#c9a227"], from: [3, 10], to: [3, 18] },
  { id: "easter", label: "Easter", colors: ["#f9d5e5", "#c7e9f5", "#fff3b0", "#cfe8c8"], from: [3, 28], to: [4, 12] },
  { id: "july4", label: "4th of July", colors: ["#b22234", "#f4f6fb", "#3c3b6e", "#d7dcee"], from: [6, 28], to: [7, 6] },
  { id: "halloween", label: "Halloween", colors: ["#f26b1d", "#4a1d6e", "#1a1020", "#f7b733"], from: [10, 18], to: [11, 1] },
  { id: "thanksgiving", label: "Thanksgiving", colors: ["#c8651b", "#8a3b12", "#f2c14e", "#e6d3b3"], from: [11, 15], to: [11, 30] },
  { id: "christmas", label: "Christmas", colors: ["#0f5132", "#b3202e", "#d4af37", "#f3f7f2"], from: [12, 1], to: [12, 26] },
];

const KEY = "rh.theme";
const DEFAULT = { kind: "none", id: null, colors: [], auto: true, enabled: true };
let state = load();
const subs = new Set();
const emit = () => subs.forEach((fn) => fn());

function load() {
  try { return { ...DEFAULT, ...JSON.parse(localStorage.getItem(KEY) || "{}"), enabled: true }; } catch { return { ...DEFAULT }; }
}
const save = (next) => {
  state = next; emit();
  const { enabled, ...persist } = next;
  localStorage.setItem(KEY, JSON.stringify(persist));
  pushPref("theme", persist);
};
onPrefs("theme", {
  apply: (v) => { state = { ...DEFAULT, ...v, enabled: state.enabled }; emit(); localStorage.setItem(KEY, JSON.stringify(v)); },
  read: () => { const { enabled, ...persist } = state; return persist; },
});

/* Holiday whose date window contains `d` (windows may wrap the year end). */
export function holidayFor(d = new Date()) {
  const md = (d.getMonth() + 1) * 100 + d.getDate();
  return HOLIDAYS.find(({ from, to }) => {
    const a = from[0] * 100 + from[1], b = to[0] * 100 + to[1];
    return a <= b ? md >= a && md <= b : md >= a || md <= b;
  }) || null;
}

export const useTheme = () => useSyncExternalStore((fn) => { subs.add(fn); return () => subs.delete(fn); }, () => state);

/* Transient hover preview (not persisted): colours shown until cleared. */
let preview = null;
const pSubs = new Set();
export const setPreview = (colors) => { if (preview !== colors) { preview = colors; pSubs.forEach((fn) => fn()); } };
export const usePreview = () => useSyncExternalStore((fn) => { pSubs.add(fn); return () => pSubs.delete(fn); }, () => preview);

/* Resolved theme: manual pick wins; otherwise the in-season holiday when auto is on. */
export function activeTheme(s = state) {
  if (!s.enabled) return null;
  if (s.kind === "holiday" || s.kind === "art") return s.colors.length ? s : null;
  if (s.auto) { const h = holidayFor(); return h ? { kind: "holiday", id: h.id, colors: h.colors } : null; }
  return null;
}

export const setHoliday = (id) => { const h = HOLIDAYS.find((x) => x.id === id); if (h) save({ ...state, kind: "holiday", id, colors: h.colors }); };
export const setAuto = (auto) => save({ ...state, auto, kind: auto ? "none" : state.kind, id: auto ? null : state.id, colors: auto ? [] : state.colors });
export const resetTheme = () => save({ ...state, kind: "none", id: null, colors: [], auto: false });
export const setThemesEnabled = (enabled) => { if (enabled !== state.enabled) { state = { ...state, enabled }; emit(); } };

/* Pull a handful of random covers from the library and build a palette from their dominant colours. */
export async function shuffleArtTheme() {
  const { data } = await api.get("/albums", { params: { sort: "random", limit: 24 } });
  const ids = (data.items || []).filter((a) => a.has_cover).slice(0, 5).map((a) => a.id);
  if (!ids.length) return false;
  const cols = await Promise.all(ids.map((id) => dominantColor(coverUrl(id))));
  const seen = new Set(), colors = [];
  for (const c of cols) { const k = rgb(c); if (!seen.has(k)) { seen.add(k); colors.push(k); } }
  if (colors.length < 2) colors.push("#f5f0e6");
  save({ ...state, kind: "art", id: Date.now(), colors, albumIds: ids });
  return true;
}

/* Theme from a single album's cover (used by Now Playing). */
export async function themeFromAlbum(albumId) {
  const cols = await palette(coverUrl(albumId), 4);
  const colors = cols.map(rgb);
  if (colors.length < 2) colors.push("#f5f0e6");
  save({ ...state, kind: "art", id: Date.now(), colors, albumIds: [albumId] });
}

/* Re-apply a saved palette ({id, colors}); id is tagged so the menu can show it as active. */
export const applyPalette = (p) => save({ ...state, kind: "art", id: `saved:${p.id}`, colors: p.colors, albumIds: [] });
export const loadThemeColors = () => state.colors;
/* A saved palette was deleted: keep the colours on screen but drop the saved tag so it can be re-saved. */
export const untagPalette = (id) => { if (state.id === `saved:${id}`) save({ ...state, id: Date.now() }); };
